import { useAuth0 } from '@auth0/auth0-react'
import { useProvidersAndTaxonomiesCountsLazyQuery } from '@generated/graphql'
import { setCounts } from '@store/slices/counts'
import { countsSelector } from '@store/slices/counts/selectors'
import {
  currentNetworkIdSelector,
  networksLoadingSelector,
} from '@store/slices/networks/selectors'
import { useAppDispatch, useAppSelector } from '@store/typedHooks'
import { useCallback, useEffect, useRef } from 'react'

export function useFetchCounts(refetchOnly: boolean = false) {
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useAuth0()
  const networkLoading = useAppSelector(networksLoadingSelector)
  const networkId = useAppSelector(currentNetworkIdSelector)
  const counts = useAppSelector(countsSelector)

  const isFirstCall = useRef(true)

  const [queryCounts, { refetch }] = useProvidersAndTaxonomiesCountsLazyQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: ({ newResourceCounts }) => {
      if (
        counts.providersCount !== newResourceCounts.providersCount ||
        counts.taxonomyItemsCount !== newResourceCounts.taxonomyItemsCount
      ) {
        dispatch(setCounts(newResourceCounts))
      }
    },
    onError: error => {
      console.error('Unable to load counts', error)
    },
  })

  useEffect(() => {
    if (!isAuthenticated || networkLoading || refetchOnly) {
      return
    }

    if (isFirstCall.current) {
      // TODO: find a better approach to increment a queries
      isFirstCall.current = false
      new Promise(() => {
        setTimeout(() => {
          queryCounts({
            variables: {
              networkId,
            },
          })
        }, 4000)
      })
    } else {
      queryCounts({
        variables: {
          networkId,
        },
      })
    }
  }, [
    isAuthenticated,
    queryCounts,
    networkLoading,
    dispatch,
    refetchOnly,
    networkId,
  ])

  const fetchCounts = useCallback(() => {
    if (!networkId) {
      return
    }

    refetch({
      networkId,
    })
  }, [networkId, refetch])

  if (refetchOnly) {
    return fetchCounts
  }
}
